import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import bowser from 'yola-bowser';
import { SIZES } from './constants';
import ColorTile from '../color-tile';
import Tooltip from '../tooltip';
import Paragraph from '../paragraph';
import Icon from '../icon';
import { AddButton } from './components/add-button';
import { parseGap } from './helpers/parse-gap';

// When collapsed and the selected option is not visible in the first row,
// move it to the last position in the first row so it stays visible without expanding
function getCollapsedOptions(options, activeIndex, maxVisible) {
  if (activeIndex >= maxVisible) {
    return [...options.slice(0, maxVisible - 1), options[activeIndex]];
  }
  return options.slice(0, maxVisible);
}

function ColorTileSelector({
  options,
  size = SIZES.extraLarge,
  value = 'indeterminate',
  onChange,
  addButtonTooltipText = '',
  onAddButtonClick = undefined,
  toggleLabels = undefined,
  onConfigureClick = undefined,
  indeterminateToolTipText = undefined,
}) {
  const initialActive = options.find((option) => value === option.id)?.id;
  const [active, setActive] = useState(initialActive);
  const [indeterminate, setIndeterminate] = useState(!initialActive);
  const [isExpanded, setIsExpanded] = useState(false);
  const [tilesPerRow, setTilesPerRow] = useState(options.length + (indeterminate ? 1 : 0));
  const containerRef = useRef(null);
  const isTouchDevice = bowser.mobile || bowser.tablet;

  const onClick = useCallback(
    (id) => {
      setActive(id);
      setIndeterminate(false);
      onChange(id);
    },
    [onChange]
  );

  const maxVisibleOptions = Math.max(0, tilesPerRow - (indeterminate ? 1 : 0));
  const shouldShowToggle = !!toggleLabels && options.length > maxVisibleOptions;

  const optionsToRender = useMemo(() => {
    if (!shouldShowToggle || isExpanded) return options;
    const activeIndex = active ? options.findIndex((option) => option.id === active) : -1;
    return getCollapsedOptions(options, activeIndex, maxVisibleOptions);
  }, [shouldShowToggle, isExpanded, options, active, maxVisibleOptions]);

  useEffect(() => {
    const newActive = options.find((option) => value === option.id)?.id;
    setActive(newActive);
    setIndeterminate(!newActive);
  }, [value, options]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const totalOptions = options.length + (indeterminate ? 1 : 0);

    const updateTilesPerRow = () => {
      const wrapperWidth = container.parentElement.getBoundingClientRect().width ?? 0;
      const optionsEl = container.querySelector('.ws-ui-color-tile-selector__options');
      if (!optionsEl) {
        setTilesPerRow(totalOptions);
        return;
      }

      const optionsStyles = window.getComputedStyle(optionsEl);
      const innerGap = parseGap(optionsStyles.gap);
      const firstTileEl = optionsEl.querySelector('.ws-ui-color-tile__wrapper, .ws-ui-color-tile');
      const tileSizePx = firstTileEl?.getBoundingClientRect().width ?? 0;

      if (wrapperWidth <= 0 || tileSizePx <= 0) {
        setTilesPerRow(totalOptions);
        return;
      }

      const addButtonWidth = onAddButtonClick ? tileSizePx + innerGap : 0;
      const availableWidth = wrapperWidth - addButtonWidth;
      const fittingCount = Math.min(
        totalOptions,
        Math.max(1, Math.floor((availableWidth + innerGap) / (tileSizePx + innerGap)))
      );

      setTilesPerRow(fittingCount);
    };

    updateTilesPerRow();

    const resizeObserver = new ResizeObserver(updateTilesPerRow);

    resizeObserver?.observe(container);
    window.addEventListener('resize', updateTilesPerRow);

    // eslint-disable-next-line consistent-return
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateTilesPerRow);
    };
  }, [toggleLabels, size, options.length, indeterminate, onAddButtonClick]);

  return (
    <div className="ws-ui-color-tile-selector-wrapper">
      <div
        style={{ '--tiles-per-row': tilesPerRow }}
        ref={containerRef}
        className={classNames('ws-ui-color-tile-selector', `ws-ui-color-tile-selector--${size}`)}
      >
        <div className="ws-ui-color-tile-selector__options">
          {optionsToRender.map((option) => {
            const tooltipId = `color-tile-tooltip-${option.id}`;
            return (
              <React.Fragment key={option.id}>
                <div
                  {...(!isTouchDevice &&
                    option.tooltipText && {
                      'data-tip': '',
                      'data-for': tooltipId,
                    })}
                >
                  <ColorTile
                    active={active === option.id}
                    size={size}
                    backgroundColor={option.backgroundColor}
                    isDarkTheme={option.isDarkTheme}
                    onClick={() => onClick(option.id)}
                    {...(onConfigureClick && {
                      onConfigureClick: () => onConfigureClick(option.id),
                    })}
                  />
                </div>
                {!isTouchDevice && option.tooltipText && (
                  <Tooltip id={tooltipId} text={option.tooltipText} />
                )}
              </React.Fragment>
            );
          })}
          {indeterminate && (
            <ColorTile
              {...(indeterminateToolTipText ? { tooltipText: indeterminateToolTipText } : null)}
              active
              indeterminate
              size={size}
              onClick={() => {}}
            />
          )}
        </div>
        {!!onAddButtonClick && (
          <AddButton size={size} tooltipText={addButtonTooltipText} onClick={onAddButtonClick} />
        )}
      </div>
      {shouldShowToggle && (
        <div
          className="ws-ui-color-tile-selector__toggle"
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          <Paragraph size="small" appearance="accent">
            {isExpanded ? toggleLabels.expanded : toggleLabels.collapsed}
          </Paragraph>
          <Icon size={18} glyph={isExpanded ? 'up' : 'down'} type="accent" />
        </div>
      )}
    </div>
  );
}

export default ColorTileSelector;
